import { buildCrossUrl, EnabledTool, getFollowupEnabledToolsUrl } from 'helpers/getFollowupEnabledToolsUrl';

import { ENVIRONMENT } from 'constants/environment';

import { PERMISSION, PermissionKey } from '../constants/PagePermission';
import { useStore } from '../mobx/helpers';

import AccountManager from 'images/assets/accountManager.svg';
import Appartamenti from 'images/assets/appartamenti.svg';
import CalendarIcon from 'images/assets/calendar.svg';
import Clients from 'images/assets/clients.svg';
import Dashboard from 'images/assets/dashboard.svg';
import GestioneInfoClientiIcon from 'images/assets/gestioneInfoClienti.svg';
import Preventivi from 'images/assets/ico_preventivi.svg';
import ProposalManager from 'images/assets/proposalManager.svg';

type SidebarItem = {
  key?: PermissionKey;
  iconName: string;
  rightIconName?: string;
  label: string;
  onClick: () => void;
  isActive: boolean;
};

const SIDEBAR_ITEMS = (
  t: any,
  navigate: (path: string) => void,
  pathname: string,
  crossCalendarUrl: string,
  tuning?: EnabledTool,
  hc?: EnabledTool,
): SidebarItem[] => {
  const homeItem = {
    key: 'home' as PermissionKey,
    iconName: 'home',
    label: t('navbarTitle.home'),
    onClick: () => navigate('/'),
    isActive: pathname === '/' || pathname === '',
  };

  const appartamentiItem = {
    key: 'apps' as PermissionKey,
    iconName: 'office-building',
    label: t('apartments.apartments'),
    onClick: () => navigate('/appartamenti'),
    isActive: pathname === '/appartamenti' || pathname.startsWith('/scheda-appartamento'),
  };

  const clientsItem = {
    key: 'clients' as PermissionKey,
    iconName: 'users',
    label: t('navbarTitle.clientManager'),
    onClick: () => navigate('/clienti'),
    isActive:
      pathname === '/clienti' ||
      pathname.startsWith('/scheda-cliente') ||
      pathname.startsWith('/modifica-cliente') ||
      pathname === '/aggiungi-cliente',
  };

  const userInfoManagerItem = {
    key: 'userInfoManager' as PermissionKey,
    iconName: 'user-circle',
    label: t('navbarTitle.userInfoManager'),
    onClick: () => navigate('/userInfoManager'),
    isActive: pathname === '/userInfoManager',
  };

  const calendarItem = {
    key: 'calendar' as PermissionKey,
    iconName: 'calendar',
    label: t('navbarTitle.calendar'),
    onClick: () => {
      // navigate to the calendar fu cross if the environment variable is set
      if (ENVIRONMENT.GO_TO_CALENDAR_FU_CROSS) {
        window.location.href = crossCalendarUrl;
        return;
      }
      navigate('/calendario');
    },
    isActive:
      pathname === '/calendario' ||
      pathname.startsWith('/dettagli-calendario') ||
      pathname.startsWith('/new-event') ||
      pathname.startsWith('/update-event'),
  };

  const accountManagerItem = {
    key: 'accountManager' as PermissionKey,
    iconName: 'account-manager',
    label: t('navbarTitle.vendorManager'),
    onClick: () => navigate('/accountManager'),
    isActive: pathname === '/accountManager',
  };

  const quoteListItem = {
    key: 'quoteList' as PermissionKey,
    // iconName: 'document-text',
    label: t('navbarTitle.listaPreventivi'),
    onClick: () => navigate('/preventivi'),
    isActive: pathname === '/preventivi',
  };

  const gestionePropItem = {
    key: 'gestione-prop' as PermissionKey,
    // iconName: 'document-duplicate',
    label: t('navbarTitle.gestioneProposta'),
    onClick: () => navigate('/gestioneProposta'),
    isActive: pathname === '/gestioneProposta',
  };

  const documentiHcItem = {
    key: 'documenti-hc' as PermissionKey,
    // iconName: 'folder',
    label: t('navbarTitle.documentiHC'),
    onClick: () => navigate('/documenti-hc'),
    isActive: pathname === '/documenti-hc',
  };

  const salesItem = {
    label: t('navbarTitle.sales'),
    iconName: 'document-text',
    hasSubItems: true,
    isActive: pathname === '/preventivi' || pathname === '/gestioneProposta' || pathname === '/documenti-hc',
    onClick: () => {},
    useDivider: tuning && tuning.enabled ? true : false,
    subItems: [quoteListItem, gestionePropItem, ...(hc && hc.enabled ? [documentiHcItem] : [])],
  };

  const items: SidebarItem[] = [homeItem, appartamentiItem, clientsItem, calendarItem, salesItem];

  if (tuning) {
    items.push({
      iconName: 'chart-bar',
      rightIconName: 'external-link',
      label: t('navbarTitle.tuning'),
      onClick: () => window.open(tuning.url, '_blank'),
      isActive: false,
    });
  }

  return items;
};

// #region ::: OPTIONS
export const options: Array<{ key: PermissionKey; url: string; icon: string; label: string }> = [
  {
    key: 'home',
    url: '/',
    icon: Dashboard,
    label: 'navbarTitle.home',
  },
  {
    key: 'apps',
    url: '/appartamenti',
    icon: Appartamenti,
    label: 'navbarTitle.apartments',
  },
  {
    key: 'clients',
    url: '/clienti',
    icon: Clients,
    label: 'navbarTitle.clientManager',
  },

  {
    key: 'userInfoManager',
    url: '/userInfoManager',
    icon: GestioneInfoClientiIcon,
    label: 'navbarTitle.userInfoManager',
  },
  {
    key: 'calendar',
    url: '/calendario',
    icon: CalendarIcon,
    label: 'navbarTitle.calendar',
  },
  {
    key: 'accountManager',
    url: '/accountManager',
    icon: AccountManager,
    label: 'navbarTitle.vendorManager',
  },
  {
    key: 'quoteList',
    url: '/preventivi',
    icon: Preventivi,
    label: 'navbarTitle.listaPreventivi',
  },
  {
    key: 'gestione-prop',
    url: '/gestioneProposta',
    icon: ProposalManager,
    label: 'navbarTitle.gestioneProposta',
  },
  {
    key: 'documenti-hc',
    url: '/documenti-hc',
    icon: Preventivi,
    label: 'navbarTitle.documentiHC',
  },
];
// #endregion

export const useNavbarRoutes = () => {
  const store = useStore();
  const navbarRoutes = options.filter((e) => {
    const page = PERMISSION[e.key];
    const hasPermission = !page || page.includes(store.loggedUser?.role);
    return hasPermission;
  });
  return navbarRoutes;
};

export const useSidebarItems = (t: (key: string) => string, navigate: (path: string) => void, pathname: string) => {
  const store = useStore();
  const enabledTools = store.enabledTools as Array<{ name: string; enabled: boolean; url: string }> | null | undefined;
  const tuning = enabledTools?.find((tool) => tool.name === 'Tuning' && tool.enabled);
  const hc = enabledTools?.find((tool) => tool.name === 'HomeConfigurator' || (tool.name === 'TailorMade' && tool.enabled));
  const crossCalendarUrl = buildCrossUrl({ toolUrl: getFollowupEnabledToolsUrl(enabledTools), module: 'calendar' });
  const allItems = SIDEBAR_ITEMS(t, navigate, pathname, crossCalendarUrl, tuning, hc);
  const filteredItems = allItems.filter((item) => {
    if (!item.key) {
      return true;
    }
    const page = PERMISSION[item.key];
    const hasPermission = !page || page.includes(store.loggedUser?.role);
    return hasPermission;
  });
  return filteredItems;
};
